/**
 * Demo-org controls (#118) — rendered ONLY when the active org doc carries
 * `isDemo: true`, which no customer org ever does. Self-gating: reads the
 * org doc itself, so no demo plumbing leaks into App/Dashboard props.
 *
 *   Reset Demo Data   — resetDemoOrg callable: wipe + reseed to known-good,
 *                       dates relative to today. Run each demo morning.
 *   Run Morning Batch — runDemoBatch callable: pushes the seeded unchecked
 *                       appointments through the real pipeline so the
 *                       cockpit cascades on cue (the scheduled sweep skips
 *                       demo orgs on purpose).
 *
 * Both callables re-verify the isDemo flag server-side — this component is
 * convenience, not the security boundary.
 */

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { isDemoOrg as readIsDemoOrg, resetDemoOrg, runDemoBatch } from '../services/organization'
import { scrubVendorText } from '../utils/scrubVendorText'

interface DemoControlsProps {
  orgId: string
  /** Called after a successful reset so the parent can refresh non-live views
   *  (the Users list is a one-time fetch, unlike the cockpit's live
   *  subscription, so restored team members wouldn't otherwise reappear). */
  onReset?: () => void
}

export default function DemoControls({ orgId, onReset }: DemoControlsProps) {
  const [isDemoOrg, setIsDemoOrg] = useState(false)
  const [busy, setBusy] = useState<'reset' | 'batch' | null>(null)

  useEffect(() => {
    let cancelled = false
    readIsDemoOrg(orgId)
      .then(isDemo => { if (!cancelled) setIsDemoOrg(isDemo) })
      .catch(() => { /* not a demo org as far as we can tell — render nothing */ })
    return () => { cancelled = true }
  }, [orgId])

  if (!isDemoOrg) return null

  const run = async (which: 'reset' | 'batch') => {
    setBusy(which)
    try {
      if (which === 'reset') {
        const { seeded } = await resetDemoOrg(orgId)
        toast.success(`Demo data reset — ${seeded} patients seeded for today and tomorrow.`)
        onReset?.()
      } else {
        const { processed, message } = await runDemoBatch(orgId)
        toast.success(message ?? `Morning batch started — ${processed} checks running.`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? scrubVendorText(err.message) : 'Demo action failed.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="demo-controls">
      <div className="demo-controls__text">
        <h3>Demo controls</h3>
        <p>
          Reset rebuilds today and tomorrow from scratch. Run Morning Batch
          sends tomorrow&apos;s unchecked patients through the pipeline live.
        </p>
      </div>
      <div className="demo-controls__actions">
        <button
          type="button"
          className="demo-controls__btn"
          disabled={busy !== null}
          onClick={() => run('batch')}
        >
          {busy === 'batch' ? 'Running batch…' : 'Run Morning Batch'}
        </button>
        <button
          type="button"
          className="demo-controls__btn demo-controls__btn--reset"
          disabled={busy !== null}
          onClick={() => run('reset')}
        >
          {busy === 'reset' ? 'Resetting…' : 'Reset Demo Data'}
        </button>
      </div>
    </div>
  )
}
